import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface Notification {
  userId: string;
  notificationId: string;
  type: string;
  photoId: string;
  commentId?: string;
  actorUserId: string;
  actorName: string;
  title: string;
  body: string;
  emoji?: string;
  read: string;
  createdAt: string;
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch all notifications
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications-all'],
    queryFn: () => api.listNotifications({ limit: 50 }),
    staleTime: 30 * 1000,
  });

  const notifications: Notification[] = notificationsData?.notifications || [];

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => api.markNotificationAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-all'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => api.markAllNotificationsAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-all'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) => api.deleteNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-all'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (notification.read === 'false') {
      await markAsReadMutation.mutateAsync(notification.notificationId);
    }

    // Navigate to the photo
    navigate(`/feed?photoId=${notification.photoId}`);
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const getNotificationIcon = (type: string, emoji?: string): string => {
    if (emoji) return emoji;
    switch (type) {
      case 'comment_reaction': return '❤️';
      case 'tag_mention': return '🏷️';
      case 'new_comment': return '💬';
      case 'new_audio': return '🎤';
      default: return '🔔';
    }
  };

  const unreadCount = notifications.filter(n => n.read === 'false').length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {unreadCount > 0 && (
          <button
            onClick={() => markAllAsReadMutation.mutate()}
            disabled={markAllAsReadMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
          >
            {markAllAsReadMutation.isPending ? 'Marking...' : 'Mark all as read'}
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      ) : notifications.length === 0 ? (
        /* Empty State */
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <div className="text-7xl mb-4">🔔</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No notifications yet</h2>
          <p className="text-gray-600 max-w-md mx-auto">
            When someone reacts to your comments or tags you in a photo, you'll see notifications here.
          </p>
        </div>
      ) : (
        /* Notifications List */
        <div className="space-y-2">
          {notifications.map((notification) => {
            const isUnread = notification.read === 'false';
            return (
              <div
                key={notification.notificationId}
                className={`bg-white rounded-xl shadow-sm border transition-all ${
                  isUnread
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-4 p-4">
                  {/* Icon */}
                  <div className="text-3xl flex-shrink-0">
                    {getNotificationIcon(notification.type, notification.emoji)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => handleNotificationClick(notification)}
                      className="text-left w-full group"
                    >
                      <p className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {notification.title}
                      </p>
                      {notification.body && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          "{notification.body}"
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {formatTimeAgo(notification.createdAt)}
                      </p>
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isUnread && (
                      <span className="w-2.5 h-2.5 bg-blue-500 rounded-full"></span>
                    )}
                    <button
                      onClick={() => deleteNotificationMutation.mutate(notification.notificationId)}
                      className="text-gray-400 hover:text-red-600 transition-colors p-1"
                      title="Delete notification"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

